import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary } from '@kollez/design-system';
import { useAnalyticsAgreement } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /platform/analytics-agreements/:id — DPDP §7.2 cross-tenant analytics
 * consent register. Renders the master fields, both signatures, permitted
 * data scope, the term, and the renewal chain (if any).
 *
 * Backed by GET /platform/analytics-agreements/:id (populates partner + tenant
 * + renewalOf + renewedBy + signatures.signedBy). Every read writes a
 * cross_tenant.read AuditLog row inside the handler.
 */

interface Signature {
  role: 'partner' | 'tenant';
  signedBy?: { fullName?: string; email?: string } | null;
  signedByName?: string;
  designation?: string;
  signedAt?: string;
  signatureUrl?: string;
}

interface Populated { _id: string; name?: string; slug?: string; displayName?: string; agreementNumber?: string; status?: string }

interface Agreement {
  _id: string;
  agreementNumber: string;
  status: string;
  partner?: Populated | null;
  tenant?: Populated | null;
  purpose?: string;
  dataScope?: string[];
  term?: { startsAt?: string; endsAt?: string };
  signatures?: Signature[];
  renewalOf?: Populated | null;
  renewedBy?: Populated | null;
  terminatedAt?: string;
  terminatedReason?: string;
  createdAt?: string;
}

function statusTone(s: string): string {
  if (s === 'Active') return 'badge-success';
  if (s === 'Signed') return 'badge-info';
  if (s === 'Expiring') return 'badge-warning';
  if (s === 'Expired' || s === 'Terminated') return 'badge-danger';
  return 'badge-outline';
}

export function PlatformAnalyticsAgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useAnalyticsAgreement(id);
  const doc = query.data as unknown as Agreement | undefined;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow"><Link to="/platform/analytics-agreements">Analytics agreements</Link></p>
          <h1 className="h-page">{doc?.agreementNumber ?? 'Loading…'}</h1>
          <p className="subhead">DPDP §7.2 · one partner ↔ one tenant, purpose-scoped, revocable</p>
        </div>
        {doc && <div className="ph-actions"><span className={`badge ${statusTone(doc.status)}`}>{doc.status}</span></div>}
      </div>

      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error} empty={!!(query.data && !doc)} emptyTitle="Not found">
        {doc && (
          <>
            <section className="card">
              <div className="card-head"><h2 className="h-section">Contract</h2></div>
              <dl className="dl">
                <dt>Partner</dt>
                <dd>{doc.partner?.name ?? '—'}</dd>
                <dt>Tenant</dt>
                <dd>{doc.tenant?.displayName ?? doc.tenant?.slug ?? '—'}</dd>
                <dt>Purpose</dt>
                <dd>{doc.purpose ?? '—'}</dd>
                <dt>Term start</dt>
                <dd>{doc.term?.startsAt ? formatIndianDate(new Date(doc.term.startsAt)) : '—'}</dd>
                <dt>Term end</dt>
                <dd>{doc.term?.endsAt ? formatIndianDate(new Date(doc.term.endsAt)) : '—'}</dd>
                <dt>Filed on</dt>
                <dd>{doc.createdAt ? formatIndianDate(new Date(doc.createdAt)) : '—'}</dd>
                {doc.terminatedAt && (<>
                  <dt>Terminated at</dt>
                  <dd>{formatIndianDate(new Date(doc.terminatedAt))}</dd>
                  <dt>Termination reason</dt>
                  <dd>{doc.terminatedReason ?? '—'}</dd>
                </>)}
              </dl>
            </section>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">Permitted data scope</h2>
                <span className="cell-sub">{(doc.dataScope ?? []).length} dimensions</span>
              </div>
              {(doc.dataScope ?? []).length === 0 ? (
                <p className="helper p-4 m-0">No scope recorded.</p>
              ) : (
                <div className="p-4">
                  <ul className="grid gap-1.5">
                    {doc.dataScope!.map((d) => (
                      <li key={d} className="text-sm"><code className="code-inline">{d}</code></li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">Signatures</h2>
                <span className="cell-sub">{(doc.signatures ?? []).length} of 2</span>
              </div>
              {(doc.signatures ?? []).length === 0 ? (
                <p className="helper p-4 m-0">Not signed yet.</p>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>Role</th><th>Name</th><th>Designation</th><th>Signed at</th></tr></thead>
                    <tbody>
                      {doc.signatures!.map((s, i) => (
                        <tr key={i}>
                          <td className="cell-title">{s.role}</td>
                          <td>{s.signedByName ?? s.signedBy?.fullName ?? '—'}</td>
                          <td className="cell-sub">{s.designation ?? '—'}</td>
                          <td className="cell-sub">{s.signedAt ? formatIndianDate(new Date(s.signedAt)) : 'pending'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {(doc.renewalOf || doc.renewedBy) && (
              <section className="card">
                <div className="card-head"><h2 className="h-section">Renewal chain</h2></div>
                <dl className="dl">
                  {doc.renewalOf && (<>
                    <dt>Renews</dt>
                    <dd><Link to={`/platform/analytics-agreements/${doc.renewalOf._id}`}>{doc.renewalOf.agreementNumber} · {doc.renewalOf.status}</Link></dd>
                  </>)}
                  {doc.renewedBy && (<>
                    <dt>Renewed by</dt>
                    <dd><Link to={`/platform/analytics-agreements/${doc.renewedBy._id}`}>{doc.renewedBy.agreementNumber} · {doc.renewedBy.status}</Link></dd>
                  </>)}
                </dl>
              </section>
            )}
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
